import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, TextField, InputAdornment,
  MenuItem, Select, FormControl, InputLabel, Tabs, Tab,
  makeStyles
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import GetAppIcon from '@material-ui/icons/GetApp';
import PublishIcon from '@material-ui/icons/Publish';
import { useDispatch, useSelector } from 'react-redux';
import {
  getListProducts, createProduct, updateProduct, deleteProduct,
  adjustStock, transferStock, uploadProductImages, deleteProductImage,
  getStockTransactions, downloadImportTemplate, importProductsFromExcel,
  resetChange
} from 'src/features/productSlice';
import { getListAgencies } from 'src/features/agencySlice';
import { showGlobalToast, showDialogConfirm, closeDialogConfirm } from 'src/features/uiSlice';
import DialogConfirm from 'src/app/components/DialogConfirm';
import { messageToastType_const } from 'src/app/constant/config';
import ProductsTable from './ProductsTable';
import ProductDialog from './ProductDialog';
import StockOverview from './StockOverview';
import StockTransactions from './StockTransactions';
import StockAdjustDialog from './StockAdjustDialog';
import StockTransferDialog from './StockTransferDialog';
import ImportExcelDialog from './ImportExcelDialog';

const PAGE_SIZE = 12;

const useStyles = makeStyles(theme => ({
  root: {
    backgroundColor: '#f4f6fa',
    minHeight: '100vh',
    padding: theme.spacing(3)
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1)
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#0C1132',
    [theme.breakpoints.down('sm')]: {
      fontSize: '18px',
      fontWeight: '500'
    }
  },
  tabBar: {
    backgroundColor: '#fff',
    marginBottom: theme.spacing(2),
    borderBottom: '1px solid #e0e0e0'
  },
  tab: {
    fontWeight: 600,
    textTransform: 'none',
    fontSize: 14,
    minWidth: 140
  },
  toolbar: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2)
  },
  searchInput: {
    flex: 1,
    minWidth: 220,
    maxWidth: 340,
    '& .MuiOutlinedInput-root': {
      height: 40,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      '& fieldset': {
        borderColor: '#E5E5E8',
        top: 0
      },
      '&:hover fieldset': {
        borderColor: '#D9042F'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F',
        borderWidth: '1px'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '10.5px 14px',
      fontSize: '14px',
      height: 40,
      boxSizing: 'border-box'
    }
  },
  filterSelect: {
    minWidth: 150,
    '& .MuiOutlinedInput-root': {
      height: 40,
      backgroundColor: '#ffffff',
      borderRadius: 4,
      '& fieldset': {
        borderColor: '#E5E5E8',
        top: 0
      },
      '&:hover fieldset': {
        borderColor: '#D9042F'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#D9042F',
        borderWidth: '1px'
      }
    },
    '& .MuiOutlinedInput-input': {
      padding: '0 14px',
      fontSize: '14px',
      height: 40,
      boxSizing: 'border-box'
    }
  },
  btnAdd: {
    color: '#ffff',
    height: '40px',
    textTransform: 'none'
  },
  btnSecondary: {
    height: '40px',
    textTransform: 'none',
    fontWeight: 600
  }
}));

const ProductsView = () => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLowStock, setFilterLowStock] = useState(false);

  // Dialog states
  const [openProductDialog, setOpenProductDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [openAdjustDialog, setOpenAdjustDialog] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openTransferDialog, setOpenTransferDialog] = useState(false);
  const [openImportDialog, setOpenImportDialog] = useState(false);

  const {
    listProducts, total, isLoading,
    statusCreate, statusUpdate, statusDelete, statusAdjust, statusTransfer
  } = useSelector(state => state.productSlice);
  const { listAgencies } = useSelector(state => state.agencySlice || {});
  const { isAdmin, isAgency, isAccountant, isSale, agencyId } = useSelector(state => state.authSlice);

  const canWrite = isAdmin || isAgency || isAccountant || isSale;

  useEffect(() => {
    dispatch(getListAgencies({ page: 1, page_size: 1000 }));
  }, [dispatch]);

  const fetchProducts = useCallback(() => {
    dispatch(getListProducts({
      page,
      page_size: PAGE_SIZE,
      keywords: search || undefined,
      category: filterCategory || undefined,
      low_stock: filterLowStock ? true : undefined
    }));
  }, [dispatch, page, search, filterCategory, filterLowStock]);

  useEffect(() => {
    if (activeTab === 0) fetchProducts();
  }, [fetchProducts, activeTab]);

  // Refresh on CRUD success
  useEffect(() => {
    if (statusDelete === 1) {
      fetchProducts();
      dispatch(resetChange());
    }
  }, [statusDelete, dispatch, fetchProducts]);

  // Close dialogs on stock success
  useEffect(() => {
    if (statusAdjust === 1) {
      setOpenAdjustDialog(false);
      fetchProducts();
      dispatch(resetChange());
    }
    if (statusTransfer === 1) {
      setOpenTransferDialog(false);
      fetchProducts();
      dispatch(resetChange());
    }
  }, [statusAdjust, statusTransfer, dispatch, fetchProducts]);

  const handleSearchChange = e => {
    setSearchInput(e.target.value);
  };

  const handleSearchKeyDown = e => {
    if (e.key === 'Enter') {
      setSearch(searchInput);
      setPage(1);
    }
  };

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setOpenProductDialog(true);
  };

  const handleOpenEdit = product => {
    setEditingProduct(product);
    setOpenProductDialog(true);
  };

  const handleDeleteProduct = product => {
    setDeleteTarget(product);
    dispatch(showDialogConfirm());
  };

  const confirmDelete = () => {
    if (deleteTarget) {
      dispatch(deleteProduct({ id: deleteTarget.id }));
      setDeleteTarget(null);
    }
    dispatch(closeDialogConfirm());
  };

  const handleOpenAdjust = (product = null) => {
    setAdjustTarget(product);
    setOpenAdjustDialog(true);
  };

  const handleOpenTransfer = () => {
    setOpenTransferDialog(true);
  };

  const handleDownloadTemplate = () => {
    dispatch(downloadImportTemplate());
  };

  const handleSaveProduct = (formData, isEdit, newImageFiles = []) => {
    if (isEdit) {
      return dispatch(updateProduct({ id: editingProduct.id, data: formData })).then(async res => {
        if (res.meta.requestStatus === 'fulfilled') {
          if (newImageFiles.length > 0 && editingProduct?.id) {
            await dispatch(uploadProductImages({ id: editingProduct.id, images: newImageFiles }));
          }
          fetchProducts();
          dispatch(resetChange());
          setOpenProductDialog(false);
          setEditingProduct(null);
        }
      });
    } else {
      return dispatch(createProduct(formData)).then(async res => {
        if (res.meta.requestStatus === 'fulfilled') {
          const payloadData = res.payload?.payload;
          const newProduct = payloadData?.data?.product
            || payloadData?.data?.products?.[0]
            || payloadData?.product
            || payloadData?.data;
          const newProductId = newProduct?.id;
          if (newProductId && newImageFiles.length > 0) {
            await dispatch(uploadProductImages({ id: newProductId, images: newImageFiles }));
          }
          fetchProducts();
          dispatch(resetChange());
          setOpenProductDialog(false);
        }
      });
    }
  };

  return (
    <Box className={classes.root}>
      {/* Header */}
      <Box className={classes.header}>
        <Typography className={classes.title}>Quản Lý Phụ Kiện</Typography>
        {isAdmin && (
          <Box display="flex" style={{ gap: 8 }}>
            <Button
              onClick={handleOpenCreate}
              style={{ color: '#ffff', height: '40px', textTransform: 'none' }}
              color="secondary"
              variant="contained"
            >
              Thêm sản phẩm
            </Button>
          </Box>
        )}
      </Box>

      {/* Tabs */}
      <Box className={classes.tabBar}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab className={classes.tab} label="Danh sách sản phẩm" />
          <Tab className={classes.tab} label="Lịch sử biến động" />
        </Tabs>
      </Box>

      {/* Tab 0: Product list */}
      {activeTab === 0 && (
        <>
          {/* Toolbar */}
          <Box className={classes.toolbar}>
            <TextField
              className={classes.searchInput}
              variant="outlined"
              size="small"
              placeholder="Tìm theo mã, tên sản phẩm..."
              value={searchInput}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: '#999' }} />
                  </InputAdornment>
                )
              }}
            />
            <TextField
              className={classes.filterSelect}
              size="small"
              select
              value={filterLowStock ? 'low' : ''}
              onChange={e => { setFilterLowStock(e.target.value === 'low'); setPage(1); }}
              SelectProps={{
                native: true
              }}
              variant="outlined"
              style={{ width: '150px' }}
            >
              <option value="">Tất cả tồn kho</option>
              <option value="low">Sắp hết hàng</option>
            </TextField>
          </Box>

          <ProductsTable
            products={listProducts}
            total={total}
            page={page}
            pageSize={PAGE_SIZE}
            isLoading={isLoading}
            canWrite={canWrite}
            isAdmin={isAdmin}
            onPageChange={setPage}
            onEdit={handleOpenEdit}
            onDelete={handleDeleteProduct}
            onAdjustStock={handleOpenAdjust}
          />
        </>
      )}

      {/* Tab 1: Transactions */}
      {activeTab === 1 && (
        <StockTransactions
          products={listProducts}
          agencies={listAgencies || []}
        />
      )}

      {/* Dialogs */}
      <ProductDialog
        open={openProductDialog}
        onClose={() => { setOpenProductDialog(false); setEditingProduct(null); }}
        editingProduct={editingProduct}
        agencies={listAgencies || []}
        defaultAgencyId={isAgency ? agencyId : ''}
        onSave={handleSaveProduct}
        dispatch={dispatch}
        uploadProductImages={uploadProductImages}
        deleteProductImage={deleteProductImage}
      />

      <StockAdjustDialog
        open={openAdjustDialog}
        onClose={() => setOpenAdjustDialog(false)}
        defaultProduct={adjustTarget}
        products={Array.isArray(listProducts) ? listProducts : []}
        agencies={listAgencies || []}
        defaultAgencyId={isAgency ? agencyId : ''}
        onAdjust={data => dispatch(adjustStock(data))}
      />

      <StockTransferDialog
        open={openTransferDialog}
        onClose={() => setOpenTransferDialog(false)}
        products={Array.isArray(listProducts) ? listProducts : []}
        agencies={listAgencies || []}
        onTransfer={data => dispatch(transferStock(data))}
      />

      <ImportExcelDialog
        open={openImportDialog}
        onClose={() => setOpenImportDialog(false)}
        agencies={listAgencies || []}
        defaultAgencyId={isAgency ? agencyId : ''}
        onImport={(file, agencyId) => dispatch(importProductsFromExcel({ file, agency_id: agencyId }))}
        onDownloadTemplate={handleDownloadTemplate}
      />

      <DialogConfirm
        title="Xác nhận xóa sản phẩm"
        textOk="Đồng ý"
        textCancel="Hủy"
        content={`Bạn có chắc chắn muốn xóa sản phẩm "${deleteTarget?.name || ''}"? Không thể hoàn tác.`}
        callbackOk={confirmDelete}
        callbackCancel={() => {
          setDeleteTarget(null);
          dispatch(closeDialogConfirm());
        }}
      />
    </Box>
  );
};

export default ProductsView;
